import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Styles Components
import {
  ContainerContent,
  ContainerData,
  ContainerDescription,
  ContainerHeader,
  ContainerOT,
  ContainerRefIcon,
  DateInput,
  DropDownCapitol,
  DropDownConcession,
  InputDescription,
  InputPKEnd,
  InputPKInit,
  RefRoid,
  RightArrow,
  TitleOrder,
  ButtonEdit,
  ButtonSave,
  LocationGroup,
  PkGroup,
  RoadSubGroup,
  ConcessionWrapper,
} from './styles';

import {
  Controller,
  ControllerProps,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import isEqual from 'lodash.isequal';
import { useSubmit } from 'react-router-dom';

// Components
import { SpecialTag } from '../../SpecialTag';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { Typography } from '../../Typography';

// Icons
import ArrowRight from '../../../../assets/icons/triangle-right.svg';

// Media
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { options as optionsMock } from '../../../../router/definitionGloblaRoutes';

// Types
import { ShowOTProps } from '../WorkOrder/types';
import { OptionSelected } from '../../../inputs/DropDown/DropDownList/types';
import { OTObject, GroupTypes } from './types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';

// GQL
import { GET_SUB_CAPITOLS_BY_IDS } from '../../../../gql/queries/orders/predefinedData';
import { client } from '../../../../gql/client';

// Validations
import { yupResolver } from '@hookform/resolvers/yup';
import { schemaOT } from '../WorkOrder/schema';
import { createFormDataFromValues } from '../../../../utils/forms';
import {
  GET_CONCESSION_BY_ID,
  GET_ROADS_BY_IDS,
} from '../../../../gql/queries/geoPositions/roads';
import * as Sentry from '@sentry/react';
import { registerPk } from '../../../../utils/pkField';

export const SubWorkOrder = ({
  id,
  roid,
  type,
  tag,
  date,
  capitol,
  subCapitol,
  concession,
  pkInit,
  pkEnd,
  road,
  roadsValues,
  capitols,
  concessions,
  description,
  notes,
  subCapitolsValues,
  modelTypeId,
}: ShowOTProps): JSX.Element => {
  const { t } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const toggleInfo = useCallback(() => setShowInfo(!showInfo), [showInfo]);
  const toggleEdit = useCallback(
    () => setIsDisabled(!isDisabled),
    [isDisabled]
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const submit = useSubmit();

  const initialData: OTObject = {
    capitols,
    concessions,
    date,
    pkInit,
    pkEnd,
    description,
    notes,
    capitol,
    concession,
    road,
    roads: roadsValues,
    subCapitol,
    subCapitols: subCapitolsValues,
  };

  const subCapitolDropDownRef = useRef<ImperativeRefInput>(null);

  const isLandscapeTablet = useIsLandscapeTablet();

  const {
    handleSubmit,
    control,
    setValue,
    register,
    formState: { errors },
  } = useForm<OTObject>({
    defaultValues: initialData,
    resolver: yupResolver(schemaOT) as any,
    // La suborden NO tiene campo de equipo → desactiva la regla yup
    // `teamRelationships` del schemaOT compartido para no bloquear su guardado.
    context: { requiresTeam: false },
  });

  const roads = useWatch({
    control,
    name: 'roads',
  });

  const subCapitols = useWatch({
    control,
    name: 'subCapitols',
  });

  const watchedFields = useWatch({
    control,
    defaultValue: initialData,
  });

  const onSelectCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const { readSubCapitols } = client.readQuery({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: option?.data ?? [],
        },
      });
      setValue('subCapitols', readSubCapitols);
      onChange(option?.value ?? '');
    },
    []
  );

  const renderSubCapitols = useCallback<
    ControllerProps<OTObject, 'subCapitol'>['render']
  >(
    ({ field: { onChange }, formState: { defaultValues } }) => {
      const options = subCapitols?.map((subCapitol) => ({
        label: subCapitol?.name || '',
        value: subCapitol?.id || '',
        data: subCapitol?.elementOrdersIds || '',
      })) || [...optionsMock];
      return (
        <DropDownInput
          ref={subCapitolDropDownRef}
          inputSize="L"
          onChangeSelected={(option) => onChange(option?.value ?? '')}
          typeDropDown="Default"
          labelText={t('workOrder.subchapter')}
          placeholder={t('common:select')}
          defaultValue={defaultValues?.subCapitol}
          options={options}
          isDisabled={isDisabled}
          borderError={!!errors.subCapitol}
        />
      );
    },
    [subCapitols, isDisabled]
  );

  const roadDropDownRef = useRef<ImperativeRefInput>(null);
  const onSelectConcession = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: option?.value },
        });

        const { readRoads } = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        });

        setValue('roads', readRoads);

        roadDropDownRef.current?.clearSelected();
        setValue('road', '');

        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onFormSubmit: SubmitHandler<OTObject> = async (data) => {
    if (!isEqual(initialData, watchedFields)) {
      setIsDisabled(true);
      const formData = createFormDataFromValues({
        ...data,
        id,
        roid,
        modelTypeId,
        type,
      });
      submit(formData, {
        method: 'POST',
        action: '.',
      });
    } else {
      setErrorMessage(
        'You must make at least one change to the form before proceeding'
      );
    }
  };

  return (
    <ContainerOT>
      <ContainerHeader onClick={toggleInfo}>
        <TitleOrder>{'Sub-Ordre de Treball'}</TitleOrder>
        <ContainerRefIcon>
          <RefRoid>{roid}</RefRoid>
          <SpecialTag
            variant={tag}
            sizeTag={isLandscapeTablet ? 'small' : 'default'}
          />

          <RightArrow
            src={ArrowRight}
            alt={t('common:expandInfo')}
            showInfo={showInfo}
          />
        </ContainerRefIcon>
      </ContainerHeader>
      {showInfo && (
        <ContainerContent onSubmit={handleSubmit(onFormSubmit)}>
          <ContainerData>
            <DateInput
              label={t('workOrder.registerDate')}
              labelHour={t('workOrder.hour')}
              isDisabled={isDisabled}
              initialDate={date}
              borderError={!!errors.date}
            />
            <Controller
              control={control}
              name="capitol"
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options = defaultValues?.capitols?.map((capitol) => ({
                  label: capitol?.name || '',
                  value: capitol?.id || '',
                  data: capitol?.subCapitolsIDs || '',
                })) || [...optionsMock];
                return (
                  <DropDownCapitol
                    inputSize={'M'}
                    onChangeSelected={(option) =>
                      onSelectCapitol(option, onChange)
                    }
                    typeDropDown="Default"
                    labelText={t('workOrder.chapter')}
                    placeholder={t('common:select')}
                    defaultValue={defaultValues?.capitol}
                    options={options}
                    isDisabled={isDisabled}
                    borderError={!!errors.capitol}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="subCapitol"
              render={renderSubCapitols}
            />

            <LocationGroup>
              <ConcessionWrapper>
                <Controller
                  control={control}
                  name="concession"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      defaultValues?.concessions?.reduce((acc, curr) => {
                        if (curr?.name !== 'CX') {
                          acc.push({
                            label: curr?.name || '',
                            value: curr?.id || '',
                          });
                        }

                        return acc;
                      }, [] as GroupTypes[]) || [];
                    return (
                      <DropDownConcession
                        inputSize={'XS'}
                        onChangeSelected={(option) =>
                          onSelectConcession(option, onChange)
                        }
                        typeDropDown="Default"
                        labelText={t('user.concession')}
                        placeholder={t('common:select')}
                        defaultValue={defaultValues?.concession}
                        options={options}
                        isDisabled={isDisabled}
                        borderError={!!errors.concession}
                      />
                    );
                  }}
                />
              </ConcessionWrapper>
              <RoadSubGroup>
                <Controller
                  control={control}
                  name="road"
                  render={({
                    field: { onChange },
                    formState: { defaultValues },
                  }) => {
                    const options =
                      roads?.map((road) => ({
                        label: road?.name || '',
                        value: road?.id || '',
                      })) || [];

                    return (
                      <DropDownInput
                        ref={roadDropDownRef}
                        inputSize="XS"
                        onChangeSelected={(option) =>
                          onChange(option?.value ?? '')
                        }
                        typeDropDown="Default"
                        labelText={t('workOrder.road')}
                        placeholder={t('common:select')}
                        defaultValue={defaultValues?.road}
                        borderError={!!errors.road}
                        options={options}
                        isDisabled={isDisabled}
                      />
                    );
                  }}
                />
              </RoadSubGroup>
            </LocationGroup>

            <PkGroup>
              <InputPKInit
                {...registerPk(register, setValue, 'pkInit')}
                label={t('workOrder.pkInitial')}
                typeInput="normal"
                disabled={isDisabled}
                defaultValue={pkInit}
                borderError={!!errors.pkInit}
              />
              <InputPKEnd
                {...registerPk(register, setValue, 'pkEnd')}
                label={t('workOrder.pkFinal')}
                typeInput="normal"
                disabled={isDisabled}
                defaultValue={pkEnd}
                borderError={!!errors.pkEnd}
              />
            </PkGroup>
          </ContainerData>
          <ContainerDescription>
            <InputDescription
              {...register('description')}
              label={t('workOrder.description')}
              disabled={isDisabled}
              defaultValue={description}
              onChange={(e) => console.log('description', e.target.value)}
              borderError={!!errors.description}
            />
            {notes && (
              <InputDescription
                {...register('notes')}
                label={t('workOrder.notes')}
                disabled={isDisabled}
                defaultValue={notes}
                borderError={!!errors.notes}
              />
            )}
            {isDisabled ? (
              <ButtonEdit variant={'edit'} onClick={toggleEdit} type="button" />
            ) : (
              <ButtonSave variant={'save'} type="submit" />
            )}
          </ContainerDescription>
          {errorMessage && (
            <Typography colorText="lightCoralRed" size="XS">
              {errorMessage}
            </Typography>
          )}
        </ContainerContent>
      )}
    </ContainerOT>
  );
};
